import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Delete } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Alert, Box, Grid, Paper, Typography } from '@mui/material';
import CustomIconButton from 'components/elements/IconButton';
import InputElement from 'components/elements/InputElement';
import SelectElement from 'components/elements/SelectElement';
import { useDrag, useDrop } from 'react-dnd';
import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { getRandomLightColor } from '../colorUtils';

const signeeType = ['Realtor', 'Lawyer', 'Tenant', 'Landlord'];

interface FormValues {
  fields: { name: string; email: string; signeeType: string }[];
}

const ItemType = 'ROW';

const DraggableRow = ({
  item,
  index,
  moveItem,
  remove,
  control,
  append,
  isLastRow,
  errors,
}: any) => {
  const [bgColor, setBgColor] = useState<string>('');

  useEffect(() => {
    setBgColor(getRandomLightColor());
  }, []);

  // Drag source
  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Drop target
  const [, drop] = useDrop({
    accept: ItemType,
    hover: (draggedItem: any) => {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <Paper
      ref={(node) => drag(drop(node))}
      style={{
        padding: '10px',
        marginBottom: '10px',
        backgroundColor: isDragging ? 'lightgray' : 'rgb(252, 252, 252)',
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <Grid container spacing={2} justifyContent="center">
        {/* Row 1: Main content */}
        <Grid item xs={1}>
          <Box
            display="flex"
            height={24}
            alignItems="center"
            justifyContent="center"
            width={24}
            sx={{ borderRadius: '50%', opacity: 0.6 }}
            bgcolor={bgColor}
          >
            <Typography variant="subtitle2">{index + 1}</Typography>
          </Box>
        </Grid>
        <Grid item xs={3}>
          <InputElement
            name={`fields.${index}.name`}
            control={control}
            label="Name"
            required
          />
        </Grid>
        <Grid item xs={3}>
          <InputElement
            name={`fields.${index}.email`}
            control={control}
            label="Email"
            required
          />
        </Grid>
        <Grid item xs={3}>
          <SelectElement
            name={`fields.${index}.signeeType`}
            control={control}
            label="Signer profile"
            required
            // placeholder="Signer profile"
            options={signeeType.map((type) => ({ value: type, label: type }))}
          />
        </Grid>
        <Grid item xs={1}>
          <Box display="flex" gap={1}>
            <CustomIconButton
              buttonType="error"
              tooltipTitle="Remove"
              onClick={() => remove(index)}
            >
              <Delete />
            </CustomIconButton>
            {isLastRow && (
              <CustomIconButton
                buttonType="primary"
                tooltipTitle="Add more"
                onClick={() => append({ name: '', email: '', signeeType: '' })}
              >
                <AddCircleIcon />
              </CustomIconButton>
            )}
          </Box>
        </Grid>

        {/* Row 2: Error messages (only if there are errors) */}
        {(errors?.fields?.[index]?.name?.message ||
          errors?.fields?.[index]?.email?.message ||
          errors?.fields?.[index]?.signeeType?.message) && (
          <Grid item xs={11} sx={{ ml: 1 }}>
            <Box sx={{ mt: 1 }}>
              {errors?.fields?.[index]?.name?.message && (
                <Typography variant="caption" color="error" display="block">
                  Name: {errors.fields[index].name.message}
                </Typography>
              )}
              {errors?.fields?.[index]?.email?.message && (
                <Typography variant="caption" color="error" display="block">
                  Email: {errors.fields[index].email.message}
                </Typography>
              )}
              {errors?.fields?.[index]?.signeeType?.message && (
                <Typography variant="caption" color="error" display="block">
                  Signer Profile: {errors.fields[index].signeeType.message}
                </Typography>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

interface DynamicFormProps {
  setContractDetails: (data: any) => void;
  initialData?: any;
  requiredSignees?: number; // Number of signees required for this level
  levelIndex?: number; // Which level this form represents
  allLevelsData?: any[]; // Data from all levels for email uniqueness validation
}

export interface DynamicFormRef {
  validateStep: () => boolean;
  validateAndGetValues: () => Promise<any>;
  getAllEmails: () => string[]; // Get all emails from this level for global validation
}

const DynamicForm = forwardRef<DynamicFormRef, DynamicFormProps>(
  (
    {
      setContractDetails,
      initialData,
      requiredSignees = 1,
      levelIndex = 0,
      allLevelsData = [],
    },
    ref
  ) => {
    const [validationError, setValidationError] = useState('');

    // Create dynamic validation schema based on current level and all levels data
    const createValidationSchema = () => {
      return yup.object().shape({
        fields: yup
          .array()
          .of(
            yup.object().shape({
              name: yup
                .string()
                .required('Name is required')
                .max(15, 'Max length is 15 characters'),
              email: yup
                .string()
                .email('Invalid email')
                .required('Email is required')
                .test(
                  'unique-email',
                  'Email must be unique across all levels',
                  function (value) {
                    if (!value) return true; // Skip validation if email is empty

                    // Check for duplicates within the same level
                    const currentLevelFields = this.parent?.fields || [];
                    const duplicateCount = currentLevelFields.filter(
                      (field: any) => field.email === value
                    ).length;
                    if (duplicateCount > 1) {
                      return this.createError({
                        message: 'Email must be unique within this level',
                      });
                    }

                    // Check for duplicates across all other levels
                    for (let i = 0; i < allLevelsData.length; i++) {
                      if (i === levelIndex) continue; // Skip current level

                      const levelData = allLevelsData[i];
                      if (levelData && levelData.fields) {
                        const hasDuplicate = levelData.fields.some(
                          (field: any) => field.email === value
                        );
                        if (hasDuplicate) {
                          return this.createError({
                            message: 'Email is already used in another level',
                          });
                        }
                      }
                    }

                    return true;
                  }
                ),
              signeeType: yup.string().required('Signee type is required'),
            })
          )
          .required()
          .test(
            'no-duplicate-emails',
            'No duplicate emails allowed anywhere',
            function () {
              const fields = this.parent?.fields || [];
              const emails = fields
                .map((field: any) => field.email?.trim().toLowerCase())
                .filter(Boolean);

              // Check for duplicates within this level
              const uniqueEmails = new Set(emails);
              if (emails.length !== uniqueEmails.size) {
                return this.createError({
                  message: 'Duplicate emails found within this level',
                });
              }

              // Check for duplicates across all levels
              for (let i = 0; i < allLevelsData.length; i++) {
                if (i === levelIndex) continue;

                const levelData = allLevelsData[i];
                if (levelData && levelData.fields) {
                  const otherLevelEmails = levelData.fields
                    .map((field: any) => field.email?.trim().toLowerCase())
                    .filter(Boolean);

                  const hasDuplicate = emails.some((email: string) =>
                    otherLevelEmails.includes(email)
                  );
                  if (hasDuplicate) {
                    return this.createError({
                      message: 'Email already used in another level',
                    });
                  }
                }
              }

              return true;
            }
          ),
      });
    };

    const {
      control,
      handleSubmit,
      getValues,
      trigger,
      watch,
      formState: { errors },
    } = useForm<FormValues>({
      defaultValues: initialData || {
        fields: [{ name: '', email: '', signeeType: '' }],
      },
      resolver: yupResolver(createValidationSchema()),
      mode: 'onChange',
    });

    // Re-validate when allLevelsData changes to catch cross-level email duplicates
    useEffect(() => {
      if (allLevelsData.length > 0) {
        trigger();
      }
    }, [allLevelsData, trigger]);

    const { fields, append, remove, move } = useFieldArray({
      control,
      name: 'fields',
    });

    // Watch form values and update parent component (contractDetails)
    useEffect(() => {
      const subscription = watch((values) => {
        setContractDetails((prev: any) => ({
          ...prev,
          dynamicFormValues: values.fields,
        }));
      });

      return () => subscription.unsubscribe();
    }, [watch, setContractDetails]);

    const validateStep = (): boolean => {
      // Check if we have at least the required number of signees
      if (fields.length < requiredSignees) {
        setValidationError(
          `Level ${levelIndex + 1} requires at least ${requiredSignees} signee${requiredSignees > 1 ? 's' : ''}. Currently have ${fields.length}.`
        );
        return false;
      }

      // Check if all fields are valid
      const hasFormErrors = Object.keys(errors).length > 0;
      if (hasFormErrors) {
        setValidationError(
          `Please fix all validation errors in Level ${levelIndex + 1} before proceeding.`
        );
        return false;
      }

      // Check if all required fields are filled
      const values = getValues();
      const hasEmptyFields = values.fields.some(
        (field) =>
          !field.name.trim() || !field.email.trim() || !field.signeeType
      );

      if (hasEmptyFields) {
        setValidationError(
          `Please fill in all required fields in Level ${levelIndex + 1}.`
        );
        return false;
      }

      // Check for email duplicates within the same level
      const emails = values.fields.map((field) =>
        field.email.trim().toLowerCase()
      );
      const uniqueEmails = new Set(emails);

      if (emails.length !== uniqueEmails.size) {
        const duplicateEmails = emails.filter(
          (email, index) => emails.indexOf(email) !== index
        );
        setValidationError(
          `Duplicate emails found in Level ${levelIndex + 1}: ${duplicateEmails.join(
            ', '
          )}. Each signee must have a unique email.`
        );
        return false;
      }

      // Check for email duplicates across all other levels
      for (let i = 0; i < allLevelsData.length; i++) {
        if (i === levelIndex) continue; // Skip current level

        const levelData = allLevelsData[i];
        if (levelData && levelData.fields) {
          const otherLevelEmails = levelData.fields.map((field: any) =>
            field.email.trim().toLowerCase()
          );

          const hasDuplicate = emails.some((email) =>
            otherLevelEmails.includes(email)
          );
          if (hasDuplicate) {
            setValidationError(
              `Email already used in another level. Each signee must have a unique email across all levels.`
            );
            return false;
          }
        }
      }

      setValidationError('');
      return true;
    };

    useImperativeHandle(ref, () => ({
      validateStep,
      validateAndGetValues: async () => {
        // First trigger form validation to populate errors
        const isValid = await trigger();
        if (!isValid) {
          // If form validation fails, show the first error
          const firstError = Object.values(errors)[0];
          if (firstError) {
            setValidationError(
              `Please fix validation errors: ${firstError.message || 'Invalid input'}`
            );
          }
          return null;
        }

        // Then check our custom validation logic
        if (validateStep()) {
          return getValues();
        }
        return null;
      },
      getAllEmails: () => {
        const values = getValues();
        return values.fields
          .map((field) => field.email.trim().toLowerCase())
          .filter(Boolean);
      },
    }));

    return (
      <form>
        {validationError && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {validationError}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Required: {requiredSignees} signee{requiredSignees > 1 ? 's' : ''} •
            Current: {fields.length}
          </Typography>
        </Box>

        {fields.map((item, index) => (
          <DraggableRow
            key={item.id}
            item={item}
            index={index}
            moveItem={move}
            remove={remove}
            control={control}
            append={append}
            isLastRow={index === fields.length - 1}
            errors={errors}
          />
        ))}
      </form>
    );
  }
);

DynamicForm.displayName = 'DynamicForm';

export default DynamicForm;
